import { parse } from "date-fns";

import {
  Author,
  EventType,
  IAssignment,
  IAttachment,
  IEvent,
  IEventAuthor,
  IEventAuthorCustomerType,
  IIntegrations,
  IReplyPaths,
  IRequester,
  ISimilarTicket,
  ITicket,
  ITicketDeliveryStatus,
  ITicketSource,
  ITicketSpamStatus,
  Rating,
  TicketPriority,
  TicketSilo,
  TicketStatus,
} from "interfaces/entities/ticket";
import { normalizeBrowserLink } from "utils/string";

const eventTypesMap = {
  message: EventType.Message,
  attachments: EventType.Message,
  assignment: EventType.Assignment,
  status: EventType.StatusChanged,
  subject: EventType.SubjectChanged,
  requester: EventType.RequesterChanged,
  ratingRequestSent: EventType.RatingRequestSent,
  followUpSent: EventType.FollowUpSent,
  rating: EventType.Rating,
  spam: EventType.Spam,
  cc: EventType.CcChanged,
  priority: EventType.PriorityChanged,
  tags: EventType.TagsChanged,
  followers: EventType.FollowersChanged,
  childTickets: EventType.ChildTickets,
  silo: EventType.SiloChanged,
  summary: EventType.Summary,
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deserializeDeliveryStatus(statuses: any[]): ITicketDeliveryStatus[] {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return statuses.map((status: any) => ({
    entityId: status.entityID,
    entityType: /@/.test(status.entityID) ? Author.Customer : Author.Agent,
    status: status.status,
    date: parse(status.date),
  }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replaceAttachments(html: string, files: { [key: string]: any }[]): string {
  // @ts-expect-error TS(6133): 'match' is declared but its value is never read.
  return html.replace(/<img ([^>]*)src="cid:([^"]+)"([^>]*)>/g, (match, firstAttrs, cid, rest) => {
    const foundAttachment = files.find(file => cid === file.cid);
    if (foundAttachment) {
      return `<img ${firstAttrs}src="${foundAttachment.url}"${rest}>`;
    }
    return "";
  });
}

export function addExternalLinks(html: string): string {
  return html.replace(/<a ([^>]*)href="([^"]+)"([^>]*)>/g, (match, firstAttrs, href, rest) => {
    const { success, url } = normalizeBrowserLink(href);

    if (success && /^.+:\/\//.test(url)) {
      return `<a ${firstAttrs}href="${url.trim()}" target="_blank" rel="nofollow noreferrer noopener"${rest}>`;
    }

    return match;
  });
}

export function countTicketsWithRating(events: IEvent[]): number {
  return events.reduce((acc, event) => {
    if (event.rating != null) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export default abstract class TicketSerializer {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static deserializeAssignment(assignment: { [key: string]: any }): IAssignment {
    return {
      agent: assignment.agent ? { id: assignment.agent.ID } : null,
      // @ts-expect-error TS(2322): Type '{ id: any; } | null' is not assignable to ty... Remove this comment to see the full error message
      team: assignment.team ? { id: assignment.team.ID } : null,
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static deserializeEventAuthor(author: any, requester: IRequester): IEventAuthor {
    if (author.type === Author.System) {
      return {
        id: author.ID,
        type: Author.System,
      };
    }

    if (author.type === Author.Rule) {
      return {
        id: author.ID,
        name: requester.name,
        type: Author.Rule,
      };
    }

    if (author.type === Author.Customer) {
      const isRequester = author.email === requester.email;
      return {
        id: author.ID,
        name: isRequester ? requester.name : author.name,
        email: isRequester ? requester.email : author.email,
        type: Author.Customer,
        customerType: isRequester ? IEventAuthorCustomerType.Requester : IEventAuthorCustomerType.Cc,
      };
    }

    return {
      id: author.ID,
      name: author.name,
      type: Author.Agent,
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static deserializeEventType(type: any): EventType | null {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return eventTypesMap[type] || null;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static mergeChildTicketEvents(events: { [key: string]: any }[]): any[] {
    let newEvents = [...events];

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let cursorStart: any = null;
    newEvents.forEach((event, i) => {
      if (event.childTickets && cursorStart === null) {
        cursorStart = i;
      }

      if (cursorStart && event.childTickets && (i + 1 === newEvents.length || !newEvents[i + 1].childTickets)) {
        const oldestEvent = newEvents[cursorStart];
        const newestEvent = { ...event };

        newestEvent.childTickets.old = oldestEvent.childTickets.old;
        for (let toDeleteIdx = cursorStart; toDeleteIdx < i; toDeleteIdx += 1) {
          delete newEvents[toDeleteIdx];
        }

        cursorStart = null;
      }
    });

    newEvents = newEvents.filter(Boolean);

    return newEvents;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static deserializeEvents(events: { [key: string]: any }[], requester: IRequester): IEvent[] {
    if (!events || !Array.isArray(events)) {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'IEvent[]'.
      return null;
    }

    let newEvents = [...events];
    newEvents.forEach((event, i) => {
      if (event.attachments && newEvents[i - 1] && newEvents[i - 1].date === event.date) {
        newEvents[i - 1].attachments = event.attachments;
        newEvents[i - 1].attachments.eventID = event.ID;
        delete newEvents[i];
      }
    });
    newEvents = newEvents.filter(Boolean);
    newEvents = TicketSerializer.mergeChildTicketEvents(newEvents);

    // @ts-expect-error TS(2322): Type '{ childTickets?: { old: any; new: any; diff:... Remove this comment to see the full error message
    return newEvents.map(event => ({
      id: event.ID,
      date: parse(event.date),
      type: TicketSerializer.deserializeEventType(event.type),
      ...(event.author != null && { author: TicketSerializer.deserializeEventAuthor(event.author, requester) }),
      ...(event.message != null && {
        isPrivate: event.message.isPrivate,
        messageText: event.message.text,
        messageHtml: event.message.richTextHtml
          ? addExternalLinks(
              replaceAttachments(event.message.richTextHtml, event.attachments ? event.attachments.files : [])
            )
          : null,
        messageRichText: event.message.richTextObj,
        stripped: event.message.stripped,
      }),
      ...(event.summary != null && {
        summary: {
          text: event.summary.text,
          html: event.summary.richTextHtml,
          richText: event.summary.richTextObj,
        },
      }),
      ...(event.status != null && { status: event.status }),
      ...(event.silo != null && { silo: event.silo }),
      ...(event.deliveryStatus != null && {
        deliveryStatus: deserializeDeliveryStatus(event.deliveryStatus),
      }),
      ...(event.subject != null && { subject: event.subject }),
      ...(event.requester != null && { requester: event.requester }),
      ...(event.source != null && { source: event.source }),
      ...(event.spam != null && {
        spam: {
          old: TicketSerializer.deserializeSpam(event.spam.old),
          new: TicketSerializer.deserializeSpam(event.spam.new),
        },
      }),
      ...(event.tags != null && { tags: event.tags }),
      ...(event.assignment != null && { assignment: event.assignment }),
      ...(event.rating != null && { rating: event.rating }),
      ...(event.followers != null && { followers: event.followers }),
      ...(event.ratingRequest != null && { ratingRequest: event.ratingRequest }),
      ...(event.attachments != null &&
        event.attachments.files.length > 0 && {
          isPrivate: event.attachments.isPrivate,
          attachments: TicketSerializer.deserializeAttachments(
            event.attachments.files,
            event.attachments.eventID ?? event.ID
          ),
        }),
      ...(event.cc != null && { cc: event.cc }),
      ...(event.priority != null && { priority: event.priority }),
      ...(event.childTickets != null && {
        childTickets: {
          old: event.childTickets.old,
          new: event.childTickets.new,
          diff: event.childTickets.new.filter(
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (newChild: any) => !event.childTickets.old.find((old: any) => newChild.shortID === old.shortID)
          ),
        },
      }),
      ...(event.replySuggestionIDs != null && {
        replySuggestionIDs: event.replySuggestionIDs,
      }),
    }));
  }

  static parseAttachmentUrl(url: string): string {
    if (/^https/.test(url) === false) {
      return `https://${url}`;
    }

    return url;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeAttachments(attachments: any[], eventID: any): IAttachment[] {
    return attachments.map(attachment => ({
      contentType: attachment.content_type,
      name: attachment.name,
      url: TicketSerializer.parseAttachmentUrl(attachment.url),
      ...(attachment.width && { width: attachment.width }),
      ...(attachment.height && { height: attachment.height }),
      removedAt: attachment.removedAt ? new Date(attachment.removedAt) : null,
      external: attachment.external,
      eventID,
    }));
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeRequester(requester: { [key: string]: any }): IRequester {
    return {
      ...(requester.name && { name: requester.name }),
      email: requester.email,
    };
  }

  static deserializeStatus(status: string): TicketStatus {
    return status as TicketStatus;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeTicketSource(source: any): ITicketSource | null {
    return !source || !source?.detailedSource || (source && source?.detailedSource.toLowerCase() === "unknown")
      ? null
      : {
          ...source,
          referenceId: source.referenceID,
          referenceUrl: source.referenceURL,
          mailboxId: source.mailboxID,
          contactFormId: source.contactFormID,
        };
  }

  static deserializeRating(rating?: { rate: "good" | "neutral" | "bad" }): Rating | null {
    if (!rating) {
      return null;
    }
    return rating.rate as Rating;
  }

  static deserializeSpam(spam: ITicketSpamStatus | boolean): ITicketSpamStatus {
    if (typeof spam === "boolean") {
      return { status: spam, reason: null };
    }
    return spam;
  }

  static deserializeCCs(ccs: { email: string; name: string | null }[]): ITicket["ccs"] {
    if (ccs.length === 0) {
      return null;
    }

    return ccs.map(cc => ({
      email: cc.email,
    }));
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeIntegrations(integrations: any): IIntegrations {
    if (integrations != null && integrations.livechat && integrations.livechat.externalID) {
      return {
        livechat: {
          externalId: integrations.livechat.externalID,
        },
      };
    }
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'IIntegratio... Remove this comment to see the full error message
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeSimilarTickets(similarTickets: any[]): ISimilarTicket[] {
    return similarTickets.map(ticket => ({
      id: ticket.ID,
      subject: ticket.subject,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeReplyPaths(replyPaths: any): IReplyPaths {
    return {
      ID: replyPaths.ID,
      createdAt: replyPaths.createdAt,
      createdBy: replyPaths.createdBy,
      suggestedPaths: replyPaths.suggestedPaths,
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserialize(ticket: { [key: string]: any }): ITicket {
    const requester = TicketSerializer.deserializeRequester(ticket.requester);
    const spam = TicketSerializer.deserializeSpam(ticket.spam);

    return {
      id: ticket.ID,
      shortId: ticket.shortID,
      date: parse(ticket.createdAt),
      createdBy: ticket.createdBy,
      updatedAt: parse(ticket.updatedAt),
      updatedBy: ticket.updatedBy,
      lastMessageAt: parse(ticket.lastMessageAt),
      assignment: TicketSerializer.deserializeAssignment(ticket.assignment),
      events: TicketSerializer.deserializeEvents(ticket.events, requester),
      requester,
      status: TicketSerializer.deserializeStatus(ticket.status),
      spam,
      subject: ticket.subject,
      source: TicketSerializer.deserializeTicketSource(ticket.source),
      tagIds: ticket.tagIDs,
      teamIds: ticket.teamIDs,
      childTickets: ticket.childTickets,
      parentTicket: ticket.parentTicket,
      rating: TicketSerializer.deserializeRating(ticket.rating),
      totalRatings: countTicketsWithRating(ticket.events),
      ratingRequestSent: ticket.ratingRequestSent,
      ccs: TicketSerializer.deserializeCCs(ticket.cc),
      integrations: TicketSerializer.deserializeIntegrations(ticket.integrations),
      priority: Object.values(TicketPriority).includes(ticket.priority) ? ticket.priority : TicketPriority.Medium,
      followers: ticket.followers,
      customFields: ticket.customFields || {},
      silo: ticket.silo || TicketSilo.Tickets,
      detectedLanguage: ticket.detectedLanguage ?? null,
      ...(ticket.deliveryStatus && { deliveryStatus: ticket.deliveryStatus }),
      replyPaths: ticket.replyPaths ? TicketSerializer.deserializeReplyPaths(ticket.replyPaths) : undefined,
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deserializeCollection(tickets: any[]): ITicket[] {
    return tickets.map(ticket => TicketSerializer.deserialize(ticket));
  }
}
